/**
 * **How a Zeppelin shoots without being an aeroplane.**
 *
 * An airship is an `EncounterTarget` — a pose, an integrity value and a fire.
 * It has no `SimState`, no pilot, no angle of attack, no belt state machine and
 * no `step`, and none of that is going to change: `targets.ts`'s whole premise
 * is that forcing a gasbag through `AIRCRAFT_ROWS` means inventing fields for a
 * file that does not exist. So the question this module answers is narrow —
 * *what is the smallest thing that can hold a gun?*
 *
 * The answer was already in the codebase. `gunnery.ts`'s `GunneryShooter` is
 * **not** an aeroplane: it is `{ id, body, gun, roundsFired, roundsBefore,
 * alphaRad, damaged }`, seven fields, none of which is a `SimState`. And
 * `traverse.ts`'s `bearingsFor` takes a `GunSpec` and a body-frame direction
 * and nothing else. An airship can supply all of it — `targetBody(target)` for
 * the pose, `airshipGunSpec()` for the weapon, `alphaRad: 0` because a hull has
 * no wing, `damaged: false` because the damaged-airframe jitter is a *shot-up
 * aeroplane's* wobble and a holed gasbag does not have one.
 *
 * What an airship genuinely does not have is the thing `step` normally provides:
 * a rate accumulator and a belt. Those are §1 and §2 below.
 *
 * ## The pass, against `shooting.c+0x534` (§3 of the spec — REPORTED)
 *
 * The disassembly describes a dedicated per-tick loop with airships as shooters
 * and **every** aircraft as target — not just the player — gated on exactly
 * four tests: airship alive, target alive, target in range, a station bears. No
 * mission-type gate, no difficulty gate, no AIRCRAFT MAY BE DAMAGED gate, no
 * day/night gate was found. This pass implements those four tests and adds
 * none, which is a decision to trust a REPORTED reading; if it later turns out
 * the original *did* gate on something, this is the file that was wrong.
 *
 * It runs **inside `gun-pass.ts`**, not beside it, and that is the point:
 * airship rounds are ordinary `RoundRecord`s that join the same live list, get
 * pruned by the same cap, swept by the same `collide.ts`, drawn by the same
 * tracer path and hashed by the same `encounter-hash.ts`. There is no second
 * bullet renderer and no second projectile model. The only thing that is
 * airship-specific downstream is which RNG stream a hit's magnitude is rolled
 * on (`"airship"`, for the same reason flak rolls on `"flak"`).
 *
 * ## 1. Rate of fire, without an accumulator
 *
 * `flight/guns.ts` carries a `fireAccumulator` in `GunState` because an
 * aeroplane's trigger is an input that starts and stops. An airship's does not:
 * per §3.6 there is no trigger discipline in the loop at all — a station fires
 * whenever something it can reach is in range. So the cadence is taken straight
 * off the tick number instead:
 *
 * ```
 * roundsAtTick(t) = floor((t + 1 + phase)·dt·rate) − floor((t + phase)·dt·rate)
 * ```
 *
 * which is exact, needs no state, and cannot drift. Each station gets its own
 * `phase` so the four do not all fire on the same tick — four simultaneous
 * rounds every third tick reads as a stutter, four staggered streams read as
 * four gunners.
 *
 * ## 2. Belts, which *are* state
 *
 * 120 rounds per station, seeded at spawn and never reloaded
 * (`airship-guns.ts`), so they have to live somewhere hashable.
 * `EncounterTarget` gained two fields for it — `gunRoundsRemaining` (per
 * station) and `gunRoundsFired` (the running total, which is what gives each
 * round its `roundIndex`). Both are in `targetSnapshot`, so a replay that fired
 * different airship rounds is a replay with a different hash.
 *
 * ## 3. One station, one target
 *
 * `resolveGunnery` picks a single target for the whole aeroplane and fires
 * every bearing station at it, because an aeroplane is aimed by flying it.
 * An airship is not aimed at all — it flies straight on and its gunners
 * traverse independently — so this pass inverts the loop: **each station picks
 * its own nearest target inside its own arc.** That is both closer to §3.4's
 * per-gunner-entity loop and the thing that makes the blind spot real, because
 * a station with nothing in its arc spends no rounds and puts nothing in the
 * air.
 *
 * ## 4. The blind spot
 *
 * Nothing here implements it. It falls out of `bearingsFor` returning an empty
 * list, which falls out of `hasFixedGun: false` and the four arcs, which are
 * `MG.DAT`'s own bytes. Dead astern beyond ±131.84° azimuth only stations 1 and
 * 3 can bear, and between them they exclude |elevation| < 5.49° — so a level
 * attack from directly behind is unengageable. `airship-guns.test.ts` flies it.
 *
 * ## 5. The aim model has two terms, and only one of them is a recovered number
 *
 * `MG.DAT` contains **no accuracy field at all**, so how well a gunner shoots is
 * not something this file could transcribe even in principle. What the
 * disassembly gives is *shape*:
 *
 * - **The graze scatter**, `±(rand() & 0x7f)` per round — recovered as a value,
 *   read as BAM16 (`ZEPPELIN_GRAZE_SCATTER_BAM` argues the units out).
 * - **A per-gunner aiming mistake.** `gun-mounts-design.md` §5, from the same
 *   disassembly: `shooting.c` drives one 0x15-byte *gunner entity* per station,
 *   "each holding its own station index at `+4` and **its own aim jitter**".
 *   That the term exists is REPORTED; its magnitude was never recovered.
 *
 * Shipping only the first was measurably wrong, and the census caught it. A
 * per-round scatter around a *perfect* lead solution is far deadlier than a
 * standing bias of the same width: the rounds straddle the target instead of
 * all going to one side of it. Fired that way, a Zeppelin's gunners shot
 * **better than an ace** — `aiAimErrorRad` gives the port's reference AI hand a
 * persistent 0…1.37° error held for a whole manoeuvre, against the airship's
 * ±0.70° resampled every round — and 24 seeded sorties of each Zeppelin mission
 * went from 31/48 won to 8/48.
 *
 * So the standing bias is here too, drawn per station and held for
 * `AIRSHIP_AIM_EPOCH_ROUNDS` rounds, exactly the way `drawAimError` draws a
 * pilot's and holds it for a manoeuvre — magnitude then bearing, uniform in the
 * disc. Its scale is expressed as a multiple of `aiAimErrorRad`, so the one
 * sentence it commits to is *"an airship gunner aims about as well as an AI
 * pilot of this skill"* rather than a bare number nobody can argue with.
 * CALIBRATION-PENDING, and calibrated the way every other unrecovered gunnery
 * constant in this port was: against a census, not against a feeling.
 *
 * **This is not the recovered data being softened.** The recovered data is four
 * mounts, four arcs, 685.8 m, effectiveness 200, 120 rounds a station and a
 * ±0x7f graze — every one of them is in this port at its shipped value. The
 * bias is the term the original has that `MG.DAT` does not carry, added because
 * leaving it out was a *fidelity* error that happened to point at "harder".
 */

import type { GunMountSpec } from "../flight/guns.ts";
import { addVec3, normalizeVec3, rotateVec3, inverseRotateVec3, type Vec3 } from "../flight/math3d.ts";
import type { RngBank } from "../flight/rng.ts";
import type { Tunables } from "../flight/tunables.ts";
import { bamToRadians } from "../flight/units.ts";
import {
  airshipGunSpec,
  isArmedTargetKind,
  ZEPPELIN_ENGAGEMENT_RANGE_M,
  ZEPPELIN_GRAZE_SCATTER_BAM,
} from "./airship-guns.ts";
import type { EncounterSide } from "./encounter-sides.ts";
import { aimPoint, subtractVec3, type CombatBody } from "./geometry.ts";
import type { RoundRecord } from "./rounds.ts";
import { targetBody, type EncounterTarget } from "./targets.ts";
import { aimMount, traverseDirection, traverseOf } from "./traverse.ts";

/** The RNG stream every airship draw comes off. See `rng.ts` for why it is its own. */
export const AIRSHIP_RNG_STREAM = "airship" as const;

/**
 * Ticks of phase between one station's cadence and the next. Coprime with any
 * plausible `dt·rate` period, so four stations firing at the same rate still
 * put their rounds on four different ticks.
 */
const STATION_PHASE_TICKS = 7;

/**
 * How well an airship's gunner aims, as a multiple of `aiAimErrorRad` — the
 * angular error the port's *reference* AI pilot carries. 2.1 is the `regular`
 * tier's own `aimErrorScale` (`skill.ts`), i.e. **a Zeppelin's gunners shoot
 * like ordinary pilots**: better than a novice's 3.4, worse than a veteran's
 * 1.4, and a long way worse than an ace.
 *
 * That is the sentence this constant commits to, and it is a defensible one.
 * An airship gunner has a stable platform and no aeroplane to fly, which argues
 * up; he is shooting at a scout crossing his arc at 45 m/s from an open mount
 * in a 90 km/h slipstream, which argues down hard. Landing him at "ordinary
 * pilot" splits it. CALIBRATION-PENDING; picked by the 48-sortie census in
 * `docs/engineering/zeppelin-guns.md` §8, not from a source.
 */
export const AIRSHIP_GUNNER_AIM_ERROR_SCALE = 2.1;

/**
 * Rounds an airship fires between redraws of its gunners' standing aim error —
 * the burst a gunner holds one mistake for, and the counterpart of the AI
 * pilot's "resampled once per manoeuvre decision".
 *
 * Counted across the whole airship rather than per station, because
 * `gunRoundsFired` is the one running total in the state and a second set of
 * per-station counters would be state bought for nothing. At four stations
 * firing together that is a little over a second of fire; at one station
 * bearing, four seconds.
 */
export const AIRSHIP_AIM_EPOCH_ROUNDS = 72;

/** What the pass needs to know about one aeroplane it might shoot at. */
export interface AirshipQuarry {
  readonly id: string;
  readonly side: EncounterSide;
  readonly body: CombatBody;
  readonly destroyed: boolean;
}

export interface AirshipPassInput {
  readonly airships: readonly EncounterTarget[];
  readonly quarry: readonly AirshipQuarry[];
  readonly rng: RngBank;
  readonly tunables: Tunables;
  readonly tick: number;
  readonly dt: number;
}

/** One airship's belts and its gunners' hands after the tick — what `settleTargets` writes back. */
export interface AirshipBelt {
  readonly gunRoundsRemaining: readonly number[];
  readonly gunRoundsFired: number;
  readonly gunAimBias: readonly number[];
  readonly gunAimEpoch: number;
}

export interface AirshipFire {
  readonly rounds: readonly RoundRecord[];
  /** Keyed by airship id. Absent for an airship that did not fire — nothing to write back. */
  readonly belts: ReadonlyMap<string, AirshipBelt>;
}

const NO_FIRE: AirshipFire = { rounds: [], belts: new Map() };

/**
 * Rounds one station puts up on this tick. Pure in the tick number, so a pass
 * that is replayed from a snapshot fires the same rounds on the same ticks
 * without an accumulator ever having been serialized.
 */
export function airshipRoundsAtTick(tick: number, dt: number, roundsPerSecond: number, phaseTicks: number): number {
  if (roundsPerSecond <= 0 || dt <= 0) return 0;
  const at = (t: number): number => Math.floor((t + phaseTicks) * dt * roundsPerSecond);
  return Math.max(0, at(tick + 1) - at(tick));
}

/**
 * One axis of graze scatter, radians: `±(rand() & 0x7f)` BAM16 counts. One draw
 * gives both the magnitude and the sign — the low seven bits are the mask the
 * original applies, bit 7 is the side it falls.
 */
function grazeScatterRad(rng: RngBank): number {
  const draw = rng.nextUint32(AIRSHIP_RNG_STREAM);
  const magnitude = draw & ZEPPELIN_GRAZE_SCATTER_BAM;
  return bamToRadians(magnitude) * ((draw >>> 7) & 1 ? -1 : 1);
}

/**
 * A fresh standing aiming mistake for every station: two draws each, magnitude
 * then bearing, uniform in the disc — the same two `drawAimError` takes for an
 * AI pilot, on the `airship` stream instead of `ai`. Interleaved `[az, el, …]`
 * so one flat array is the whole hand.
 */
function drawAimBias(rng: RngBank, stations: number, tunables: Tunables): number[] {
  const span = Math.max(0, tunables.aiAimErrorRad) * AIRSHIP_GUNNER_AIM_ERROR_SCALE;
  const bias: number[] = [];
  for (let station = 0; station < stations; station++) {
    const magnitude = span * rng.nextFloat(AIRSHIP_RNG_STREAM);
    const bearing = 2 * Math.PI * rng.nextFloat(AIRSHIP_RNG_STREAM);
    bias.push(magnitude * Math.cos(bearing), magnitude * Math.sin(bearing));
  }
  return bias;
}

interface StationTarget {
  readonly quarry: AirshipQuarry;
  readonly aim: Vec3;
  readonly rangeM: number;
}

/**
 * The nearest live enemy inside the range gate that **this station can be
 * brought onto**, or `null`.
 *
 * The aim is the lead solution `aimPoint` gives every other shooter in the port
 * — `MG.DAT`'s `+5 = 1212` is the bullet-speed divisor of the original's own
 * lead solver, so an airship gunner leads its target the way a pilot does. The
 * arc test is then `aimMount(...).withinArc` on the *led* direction, which is
 * the right order: a gunner traverses onto where the aeroplane is going, and if
 * that point is outside his mount he cannot take the shot even though the
 * aeroplane itself is inside it.
 */
function stationTarget(
  body: CombatBody,
  mount: GunMountSpec,
  quarry: readonly AirshipQuarry[],
  side: EncounterSide,
  tunables: Tunables,
): StationTarget | null {
  let best: StationTarget | null = null;
  for (const entry of quarry) {
    if (entry.side === side || entry.destroyed) continue;
    const offset = subtractVec3(entry.body.position, body.position);
    const rangeM = Math.hypot(offset.x, offset.y, offset.z);
    if (rangeM <= 0 || rangeM >= ZEPPELIN_ENGAGEMENT_RANGE_M) continue;
    if (best !== null && rangeM >= best.rangeM) continue;
    const lead = aimPoint(body, entry.body, tunables.gunMuzzleSpeedMps);
    const aim = normalizeVec3(
      inverseRotateVec3(body.orientation, subtractVec3(lead.point, body.position)),
      { x: 0, y: 0, z: -1 },
    );
    if (!aimMount(mount, traverseOf(aim)).withinArc) continue;
    best = { quarry: entry, aim, rangeM };
  }
  return best;
}

/**
 * One tick of defensive fire for every armed target on the board.
 *
 * Draws from the `airship` stream **exactly twice per round fired** and not at
 * all otherwise — the same contract `resolveGunnery` keeps for `gunnery`, and
 * for the same reason: the stream must advance as a function of rounds fired,
 * never of whether the geometry happened to be interesting.
 */
export function airshipPass(input: AirshipPassInput): AirshipFire {
  const armed = input.airships.filter(
    (target) => isArmedTargetKind(target.kind) && !target.destroyed && !target.burning,
  );
  if (armed.length === 0 || input.quarry.length === 0) return NO_FIRE;

  const gun = airshipGunSpec(input.tunables.gunRoundsPerSecond);
  const tracerInterval = Math.max(1, Math.round(input.tunables.gunTracerInterval));
  const rounds: RoundRecord[] = [];
  const belts = new Map<string, AirshipBelt>();

  for (const airship of armed) {
    const body = targetBody(airship);
    const remaining = [...airship.gunRoundsRemaining];
    let roundsFired = airship.gunRoundsFired;
    let spent = false;

    // The gunners' hands, settled once for the tick. The epoch is a pure
    // function of `gunRoundsFired`, so how many draws this takes depends on the
    // state and never on the geometry — the `airship` stream's contract.
    const epoch = Math.floor(roundsFired / AIRSHIP_AIM_EPOCH_ROUNDS);
    const stale = epoch !== airship.gunAimEpoch || airship.gunAimBias.length !== gun.mounts.length * 2;
    const bias = stale ? drawAimBias(input.rng, gun.mounts.length, input.tunables) : [...airship.gunAimBias];
    if (stale) spent = true;

    gun.mounts.forEach((mount, station) => {
      const belt = remaining[station] ?? 0;
      if (belt <= 0) return;
      const wanted = airshipRoundsAtTick(
        input.tick,
        input.dt,
        gun.roundsPerSecond,
        station * STATION_PHASE_TICKS,
      );
      if (wanted <= 0) return;
      // A station that cannot be brought onto anything spends nothing. This is
      // the whole of the blind spot: no target inside the arc, no rounds.
      const engaged = stationTarget(body, mount, input.quarry, airship.side, input.tunables);
      if (!engaged) return;

      const count = Math.min(wanted, belt);
      const requested = traverseOf(engaged.aim);
      // This gunner's standing mistake, held for the whole epoch — the term the
      // module note's §5 argues for.
      const biasAz = bias[station * 2] ?? 0;
      const biasEl = bias[station * 2 + 1] ?? 0;
      for (let index = 0; index < count; index++) {
        // Two draws per round, azimuth then elevation, always in that order.
        const azimuthRad = requested.azimuthRad + biasAz + grazeScatterRad(input.rng);
        const elevationRad = requested.elevationRad + biasEl + grazeScatterRad(input.rng);
        const aimed = aimMount(mount, { azimuthRad, elevationRad });
        const roundIndex = roundsFired + index;
        rounds.push({
          shooterId: airship.id,
          roundIndex,
          from: addVec3(body.position, rotateVec3(body.orientation, mount.bodyOffsetM)),
          direction: rotateVec3(body.orientation, traverseDirection(aimed.traverse)),
          spawnTick: input.tick,
          damageRaw: gun.damageRaw,
          tracer: roundIndex % tracerInterval === 0,
        });
      }
      remaining[station] = belt - count;
      roundsFired += count;
      spent = true;
    });

    if (spent) {
      belts.set(airship.id, {
        gunRoundsRemaining: remaining,
        gunRoundsFired: roundsFired,
        gunAimBias: bias,
        gunAimEpoch: epoch,
      });
    }
  }

  return rounds.length === 0 && belts.size === 0 ? NO_FIRE : { rounds, belts };
}
